import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api';

/**
 * useOrchestrator — Centralized fetching and smart polling hook for /ops.
 * 
 * Performance:
 * - Tiered polling: Detail (15s) > List (45s) > Summary (90s).
 * - Background pause: Polling stops when tab is not visible.
 * - Selective polling: Stops Detail polling if run is terminal.
 * - Action-triggered sync: Mutations trigger immediate targeted refresh.
 */
export const useOrchestrator = (options = {}) => {
    const { 
        id, 
        runId, 
        viewMode, 
        searchTerm, 
        statusFilter, 
        triggerFilter, 
        sortBy, 
        sortOrder, 
        page 
    } = options;

    const [workflows, setWorkflows] = useState([]);
    const [workflowRuns, setWorkflowRuns] = useState([]);
    const [runDetail, setRunDetail] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, totalRecords: 0, totalPages: 1 });
    const [lastSyncedAt, setLastSyncedAt] = useState(null);
    const [isPolling, setIsPolling] = useState(false);

    const pollIntervalRef = useRef(null);

    // 1. Core Fetchers
    const fetchSummary = useCallback(async (silent = false) => {
        try {
            if (!silent) setIsPolling(true);
            const res = await api.get(`/api/workflows/dashboard/summary`);
            setSummary(res.data);
            setLastSyncedAt(new Date());
        } catch (err) {
            console.error('Summary fetch error:', err);
        } finally {
            setIsPolling(false);
        }
    }, []);

    const fetchWorkflows = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            setIsPolling(true);
            const res = await api.get(`/api/workflows`, {
                params: {
                    search: searchTerm,
                    status: statusFilter === 'ALL' ? undefined : statusFilter,
                    triggerType: triggerFilter === 'ALL' ? undefined : triggerFilter
                }
            });
            setWorkflows(res.data.workflows || []);
            setLastSyncedAt(new Date());
        } catch (err) {
            setError('Failed to load workflows.');
        } finally {
            setLoading(false);
            setIsPolling(false);
        }
    }, [searchTerm, statusFilter, triggerFilter]);

    const fetchWorkflowRuns = useCallback(async (workflowId, silent = false) => {
        const targetId = workflowId || id;
        if (!silent) setLoading(true);
        try {
            setIsPolling(true);
            const endpoint = targetId ? `/api/workflows/${targetId}/runs` : `/api/workflows/runs`;
            const res = await api.get(endpoint, {
                params: {
                    limit: 20,
                    page,
                    sortBy,
                    sortOrder,
                    status: statusFilter === 'ALL' ? undefined : statusFilter,
                    triggerSource: triggerFilter === 'ALL' ? undefined : triggerFilter,
                    search: searchTerm || undefined
                }
            });
            setWorkflowRuns(res.data.runs);
            setPagination(res.data.pagination);
            setLastSyncedAt(new Date());
        } catch (err) {
            setError('Failed to fetch runs.');
        } finally {
            setLoading(false);
            setIsPolling(false);
        }
    }, [id, page, sortBy, sortOrder, statusFilter, triggerFilter, searchTerm]);

    const fetchRunDetail = useCallback(async (targetRunId, silent = false) => {
        if (!targetRunId) return;
        try {
            setIsPolling(true);
            const res = await api.get(`/api/workflows/runs/${targetRunId}`);
            setRunDetail(res.data);
            setLastSyncedAt(new Date());
        } catch (err) {
            if (err.response?.status === 403) {
                setError('Workspace Access Denied.');
            } else if (err.response?.status === 404) {
                setError('Execution Track Not Found.');
            } else {
                setError('Failed to fetch run execution detail.');
            }
        } finally {
            setIsPolling(false);
        }
    }, []);

    // 2. Specialized Polling Orchestrator
    useEffect(() => {
        const startPolling = () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

            // Determine polling frequency based on context
            let intervalTime = 45000; // Default list polling (45s)
            if (viewMode === 'runDetail') {
                // If run is terminal, slow down polling significantly
                const status = runDetail?.run?.status;
                const isTerminal = ['COMPLETED', 'FAILED', 'CANCELED', 'RETRY_EXHAUSTED'].includes(status);
                intervalTime = isTerminal ? 120000 : 15000; // 15s for active, 2m for terminal
            } else if (viewMode === 'edit') {
                intervalTime = 300000; // 5 mins during edit (basically disabled)
            }

            pollIntervalRef.current = setInterval(() => {
                if (document.hidden) return; // Pause on background

                if (viewMode === 'runDetail') {
                    fetchRunDetail(runId, true);
                    fetchSummary(true);
                } else if (viewMode === 'runs' || viewMode === 'allRuns') {
                    fetchWorkflowRuns(id, true);
                    fetchSummary(true);
                } else if (viewMode === 'list') {
                    fetchWorkflows(true);
                    fetchSummary(true);
                }
            }, intervalTime);
        };

        startPolling();
        return () => clearInterval(pollIntervalRef.current);
    }, [viewMode, runId, id, runDetail?.run?.status, fetchRunDetail, fetchWorkflowRuns, fetchWorkflows, fetchSummary]);

    // 3. Manual Sync Handler
    const manualSync = useCallback(async () => {
        if (viewMode === 'runDetail') await fetchRunDetail(runId);
        else if (viewMode === 'runs' || viewMode === 'allRuns') await fetchWorkflowRuns(id);
        else if (viewMode === 'list') await fetchWorkflows();
        await fetchSummary();
    }, [viewMode, runId, id, fetchRunDetail, fetchWorkflowRuns, fetchWorkflows, fetchSummary]);

    // 4. Action Handlers (Mutations)
    const handleRunAction = useCallback(async (targetRunId, action, params = {}) => {
        try {
            const data = action === 'restart' ? { mode: params.mode } : undefined;
            await api.put(`/api/workflows/runs/${targetRunId}/${action}`, data);
            
            // Immediate partial sync for feedback
            if (viewMode === 'runDetail' && targetRunId === runId) {
                await fetchRunDetail(targetRunId, true);
            } else {
                await fetchWorkflowRuns(id, true);
            }
            await fetchSummary(true);
            return { success: true };
        } catch (err) {
            return { success: false, error: err.response?.data?.message || `Failed to ${action} run.` };
        }
    }, [viewMode, runId, id, fetchRunDetail, fetchWorkflowRuns, fetchSummary]);

    return {
        workflows,
        workflowRuns,
        runDetail,
        summary,
        loading,
        error,
        pagination,
        lastSyncedAt,
        isPolling,
        manualSync,
        handleRunAction,
        fetchWorkflowRuns,
        fetchRunDetail,
        fetchWorkflows,
        fetchSummary
    };
};
